RivalIQ.module('configuration', function (module, App, Backbone, Marionette, $, _, s, window) {

    'use strict';

    const sidebar = module.sidebar = {};
    const source = RivalIQ.enums.sources;

    const routes = module.sidebar.routes = {};

    // Builds a reversed hash that points from path to configuration
    // We pass all of the sidenav items and their subnav children in here and
    // extract the paths they point to. This results in a hash of all specified
    // paths to the subnav items that specify them.
    function buildRoutes(item, parent) {
        const url = _.result(item.navigation, 'url');
        const baseUrl = _.result(item.navigation, 'baseUrl');
        let fullPath = url;
        if (baseUrl) {
            fullPath = baseUrl + url;
        }

        routes[fullPath] = (parent ? parent : item);
        _.each(item.items, function (menuItem) {
            buildRoutes(menuItem, item);
        });
    }

    function getActive(path) {
        let filtered = _.filter(_.keys(routes), function (route) {
            return s.startsWith(path, s.trim(route, '#'));
        });

        // filter private / public routes to avoid public posts highling private analytics tab
        const isPrivatePath = _.includes(path, 'insight');
        filtered = _.filter(filtered, function (route) {
            return (isPrivatePath ? _.includes(route, 'insight') : !_.includes(route, 'insight'));
        });

        const route = _.minBy(filtered, function (route) {
            return s.levenshtein(path, route);
        });

        if (route && routes[route]) {
            return route;
        }

        return null;
    }

    // Sets the active menu item based on the closest path match
    function setActive() {
        const curPath = window.location.pathname + window.location.hash;
        const route = getActive(curPath);

        if (route) {
            routes[route].active = true;
        }
    }

    function routeOptions(s, requiredBetaFlag) {

        return {
            display: function () {
                const meetsBetaRequirement = (requiredBetaFlag ?
                    RivalIQ.state.account.inBeta(requiredBetaFlag) :
                    true);

                return meetsBetaRequirement && App.state.account.channelDisplayWithUpsell(s);
            },
            isBetaItem: Boolean(requiredBetaFlag)
        };
    }

    function createSection(name, items, options) {
        return _.extend({
            name: name,
            items: items
        }, options);
    }

    function createSectionItemGroup(name, items, options) {
        return _.extend({
            name: name,
            items: items
        }, options, {
            isGroup: true
        });
    }

    function createItem(name, icon, path, options) {
        // NOTE only grab page config for built-in items, not custom dashboards; see #30427
        const items = _.result(!_.get(options, 'isCustomDashboard') ? module.menus.getPageByName(name) || {} : {}, 'items');
        const base = {
            id: _.get(options, 'id'),
            name: name,
            icon: icon,
            navigation: _.extend({
                url: path
            }, _.pick(options, 'navigation')),
            items: items,
            isBetaItem: _.get(options, 'isBetaItem', false),
            showTooltip: _.get(options, 'showTooltip', false),
            customLabel: _.get(options, 'customLabel'),
            displayName: _.get(options, 'displayName')
        };

        const hash = _.extend(base, _.omit(options, 'navigation'));
        buildRoutes(hash);
        return hash;
    }

    function createItemFromSource(source, path, requiredBetaFlag, additionalRouteOptions) {

        const sourceDetails = App.enums.sourceDetails[source];

        const name = sourceDetails.name;
        const fontIcon = sourceDetails.fontIcon;
        let icon = fontIcon.substr(fontIcon.indexOf('-') + 1);
        if (icon === 'tiktok') {
            icon += ' fab';
        }

        return createItem(name, icon, path, _.extend(routeOptions(source, requiredBetaFlag), additionalRouteOptions));
    }

    // use the first navigation item in menu config as the sidebar link
    function createChannelItemFromSource(source, requiredBetaFlag, additionalRouteOptions) {
        const configurations = RivalIQ.configuration.menus.getConfiguration();
        const firstVisibleItem = _.find(_.get(configurations[source], ['items']), function (item) {
            if (item.display) {
                return item.display();
            }
            return true;
        });

        const baseUrl = _.get(firstVisibleItem, ['navigation', 'baseUrl']);
        const url = _.get(firstVisibleItem, ['navigation', 'url']);

        return createItemFromSource(App.enums.sources[source], [baseUrl, url].join(''), requiredBetaFlag, additionalRouteOptions);
    }

    function buildDashboardItems() {
        if (!RivalIQ.state.dashboards) {
            return [];
        }

        RivalIQ.state.dashboards.sort();

        let landscapeHeader = true;
        const dashboards = RivalIQ.state.dashboards.reduce(function (arr, dashboard) {

            if (landscapeHeader && dashboard.get('type') === RivalIQ.enums.visibility.LANDSCAPE) {
                arr.push({
                    className: 'mini-divider'
                });
                landscapeHeader = false;
            }

            // don't include account dashboards
            if (RivalIQ.state.user.isReadOnly() && dashboard.get('type') === RivalIQ.enums.visibility.ACCOUNT) {
                return arr;
            }

            let icon = 'th-large';
            if (dashboard.get('type') === RivalIQ.enums.visibility.ACCOUNT) {
                icon = 'sitemap';
            }

            arr.push(createItem(dashboard.get('name'), icon, `/dashboards/${dashboard.id}`, {
                showTooltip: true,
                isCustomDashboard: true
            }));

            return arr;
        }, []);

        // don't include add controls
        if (RivalIQ.state.user.isReadOnly()) {
            return dashboards;
        }

        dashboards.push({
            className: 'mini-divider'
        });
        dashboards.push(createItem('New Dashboard', 'plus', '/dashboards/templates', {
            items: [{
                navigation: {
                    url: '/dashboards/new'
                }
            }]
        }));

        return dashboards;
    }

    function createSourceFilter(sources) {
        return function () {
            return _.some(sources, function (source) {
                return App.state.account.channelDisplay(source);
            });
        };
    }

    const readOnlyConfig = function () {
        return createSection(null, _.flatten([
            createSection('Custom Dashboards', buildDashboardItems(), {
                expandable: true,
                expanded: true,
                dashboardStateEvents: {
                    'add': function () {
                        const me = this;
                        me.model.set('items', buildDashboardItems());
                    },
                    'destroy': function () {
                        const me = this;
                        me.model.set('items', buildDashboardItems());
                    },
                    'change': function () {
                        const me = this;
                        me.model.set('items', buildDashboardItems());
                    }
                },
                icon: 'tachometer'
            }),
            createSection(null, [
                // NOTE we need to disambiguate `/manage` with hash state
                // because the underlying mechanism uses path as an id
                // (which means the option below will stomp on it otherwise)
                createItem('Settings', 'gear', '/manage#subscription', {
                    enabledForExpired: true,
                    enabledForCancelled: true,
                    items: [{
                        navigation: {
                            url: '/account'
                        }
                    }]
                })
            ])
        ]));
    };

    const globalConfig = function () {

        const topSection = createSection('Landscape', [
            createItem(RivalIQ.strings.dashboard_companies_title, 'th-large', '/companies', {
                enabledForExpired: true
            }),
            createItem('Landscape Settings', 'gear', '/settings'),
            createItem('Post Tag Manager', 'tags', '/post-tag-manager', {
                className: 'hidden-sm hidden-xs'
            })
        ], {
            expandable: true,
            icon: 'th'
        });

        const privateDataItems = [
            createItem('Facebook Insights', 'facebook', '/insights/facebook', routeOptions(source.FACEBOOK_INSIGHTS)),
            createItem('Facebook Ads', 'facebook', '/ads/facebook', routeOptions(source.FACEBOOK_ADS)),
            createItem('Instagram Insights', 'instagram', '/insights/instagram', routeOptions(source.INSTAGRAM_INSIGHTS)),
            createItem('Twitter Analytics', 'twitter', '/insights/twitter', routeOptions(source.TWITTER_INSIGHTS)),
            createItem('LinkedIn Analytics', 'linkedin', '/insights/linkedin', routeOptions(source.LINKEDIN)),
            createItem('TikTok Insights', 'tiktok fab', '/insights/tiktok', _.defaults({
                id: 'tiktok_insights',
                display: () => {
                    return RivalIQ.state.account.inBeta('tiktok_private');
                },
                isBetaItem: true
            }))
        ];

        const socialSection = [
            createSection('Social Analytics', [
                createSectionItemGroup('Competitive Public Data', [
                    createChannelItemFromSource('CROSS_CHANNEL'),
                    createChannelItemFromSource('FACEBOOK'),
                    createChannelItemFromSource('INSTAGRAM'),
                    createChannelItemFromSource('TWITTER'),
                    createChannelItemFromSource('YOUTUBE'),
                    createChannelItemFromSource('TIKTOK')
                ], {
                    expandable: false,
                    icon: 'share-alt',
                    description: RivalIQ.strings.sidebar_competitive_benchmarking_description
                }),

                createSectionItemGroup('Your Private Data', privateDataItems, {
                    expandable: false,
                    icon: 'area-chart',
                    description: RivalIQ.strings.sidebar_private_data_sources_description
                })

            ], {
                // this is the only section with popovers in the title
                forcePopoverAttachment: true,
                expandable: true,
                icon: 'share-alt',
                isSection: true
            })
        ];

        return createSection(null, _.flatten([
            topSection,
            socialSection,
            createSection('Social Listening', [
                createSectionItemGroup('Social Web by Quid', [
                    createItem('Instant Search', 'search', '/listening/search', {}),
                    createItem('Saved Searches', 'bookmark', '/listening', {
                        id: 'social_listening_saved_topics'
                    }),
                    createItem('Quid Monitor Topics', 'search', '/listening/topics', _.defaults({
                        id: 'social_listening_quid_topics',
                        display: () => {
                            return RivalIQ.state.account.get('hasCompanionMode');
                        }
                    }))
                ], {
                    expandable: false,
                    icon: 'share-alt',
                    description: RivalIQ.strings.sidebar_social_web_by_quid_description
                }),
                createSectionItemGroup('Channel-Specific Search', [
                    createItem('Twitter Discover', 'twitter', '/discover/twitter', routeOptions(source.TWITTER)),
                    createItem('Instagram Discover', 'instagram', '/discover/instagram', routeOptions(source.INSTAGRAM))
                ], {
                    expandable: false,
                    display: createSourceFilter([
                        source.TWITTER,
                        source.INSTAGRAM
                    ]),
                    icon: 'share-alt',
                    description: RivalIQ.strings.sidebar_channel_specific_search_description
                })
            ], {
                expandable: true,
                icon: 'commenting'
            }),
            createSection('Custom Dashboards', buildDashboardItems(), {
                expandable: true,
                dashboardStateEvents: {
                    'add': function () {
                        const me = this;
                        me.model.set('items', buildDashboardItems());
                    },
                    'destroy': function () {
                        const me = this;
                        me.model.set('items', buildDashboardItems());
                    },
                    'change': function () {
                        const me = this;
                        me.model.set('items', buildDashboardItems());
                    }
                },
                icon: 'tachometer'
            }),
            createSection('Reports', [
                createItem('PPT / CSV Reports', 'file-text-o', '/reports'),
                createItem('Scheduled Exports', 'clock-o', '/reports/scheduled'),
                createItem('Downloads', 'cloud-download', '/downloads')
            ], {
                expandable: true,
                icon: 'file-text'
            }),
            createSection(null, [
                createItem('Alerts', 'bell', '/alerts', {
                    items: [{
                        navigation: {
                            url: '/alerts'
                        }
                    }]
                })
            ])
        ]));

    };

    sidebar.getGlobal = function () {
        const config = (RivalIQ.state.user.isReadOnly() ? readOnlyConfig() : globalConfig());

        return _.tap(config, function () {
            setActive();
        });
    };

    sidebar.getActiveSidebarPath = function (path) {
        const route = getActive(path);
        if (route) {
            return routes[route].navigation.url;
        }

        return null;
    };

}, window.s, window);
